import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import ChartResultPage from './ChartResultPage'
import { generateChartDataFromTopic, generateChartDataFromInput, validateChartData } from './chartUtils'
import { MODEL_MODES } from "../../constants/agentConfig"
import { AgentHistoryIntegration } from '../../utils/agentHistoryIntegration'
import { useCreditWarning } from '../../hooks/useCreditWarning'
import CreditWarningModal from '../../components/CreditWarningModal'
import { useSubscription } from '../../hooks/useSubscription'
import { AGENT_TYPES } from '../index.js'
import linkupService from '../../services/linkupService'

// Chart Agent States
const AGENT_STATES = {
  GENERATING: 'generating',
  COMPLETED: 'completed'
}

const ChartAgent = ({ query, onBack, llmService, options = {} }) => {
  const [currentState, setCurrentState] = useState(AGENT_STATES.GENERATING)
  const [inputType, setInputType] = useState(options.inputType || 'topic') // 'topic' or 'data'
  const [chartData, setChartData] = useState([])
  const [generatedCharts, setGeneratedCharts] = useState([])
  const [searchResults, setSearchResults] = useState([])
  const [progress, setProgress] = useState({ current: 0, total: 5 })
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [hasInitialized, setHasInitialized] = useState(false)
  const { warningModal, checkAndWarnCredits, closeWarning } = useCreditWarning()
  const { consumeCredits } = useSubscription()
  
  // Use refs to track state and prevent unnecessary re-generation
  const isGenerating = useRef(false)
  const currentQuery = useRef(query)
  const hasEverGenerated = useRef(false)

  // Session storage keys for persistence
  const getSessionKey = (suffix) => `chart_agent_${query}_${suffix}`

  // Auto-start generation when component mounts
  useEffect(() => {
    if (query && !hasEverGenerated.current && !isGenerating.current) {
      handleStartGeneration(inputType, query)
    }
  }, [query, inputType])

  // Load persisted state on component mount
  useEffect(() => {
    const loadPersistedState = () => {
      try {
        const persistedState = sessionStorage.getItem(getSessionKey('state'))
        const persistedCharts = sessionStorage.getItem(getSessionKey('charts'))
        
        if (persistedState && persistedCharts) {
          const state = JSON.parse(persistedState)
          const chartsData = JSON.parse(persistedCharts)
          
          console.log('📊 Loading persisted Chart state:', state.currentState)
          setCurrentState(state.currentState)
          setGeneratedCharts(chartsData)
          setHasInitialized(true)
          hasEverGenerated.current = true
          
          return true
        }
      } catch (error) {
        console.warn('⚠️ Failed to load persisted state:', error)
        // Clear corrupted data
        try {
          sessionStorage.removeItem(getSessionKey('state'))
          sessionStorage.removeItem(getSessionKey('charts'))
        } catch (clearError) {
          console.warn('⚠️ Failed to clear corrupted session data:', clearError)
        }
      }
      return false
    }
    
    // Only load persisted state if query matches current query
    if (currentQuery.current === query) {
      const hasPersistedState = loadPersistedState()
      if (hasPersistedState) {
        return // Skip initial generation
      }
    } else {
      // Query changed, clear old session data
      try {
        const oldQuery = currentQuery.current
        sessionStorage.removeItem(`chart_agent_${oldQuery}_state`)
        sessionStorage.removeItem(`chart_agent_${oldQuery}_charts`)
      } catch (error) {
        console.warn('⚠️ Failed to clear old session data:', error)
      }
      currentQuery.current = query
    }
    
    setHasInitialized(true)
  }, [query])

  // Persist state changes to session storage
  useEffect(() => {
    if (hasInitialized && generatedCharts.length > 0) {
      try {
        sessionStorage.setItem(getSessionKey('state'), JSON.stringify({
          currentState,
          hasInitialized: true
        }))
        sessionStorage.setItem(getSessionKey('charts'), JSON.stringify(generatedCharts))
      } catch (error) {
        console.warn('⚠️ Failed to persist state:', error)
      }
    }
  }, [currentState, generatedCharts, hasInitialized, query])

  const handleStartGeneration = async (finalInputType, finalData) => {
    // Check credits before starting generation
    const canProceed = await checkAndWarnCredits(AGENT_TYPES.CHART)
    if (!canProceed) {
      return // Stop execution if insufficient credits
    }

    setCurrentState(AGENT_STATES.GENERATING)
    setInputType(finalInputType)
    setChartData(finalData)
    setProgress({ current: 0, total: 5 })
    setIsLoading(true)
    setError(null)
    
    try {
      console.log(`🔄 Memulai generate chart untuk: ${query}`)
      console.log(`📊 Input type: ${finalInputType}`)
      
      const newGeneratedCharts = []
      const chartTypes = ['pie', 'bar', 'line', 'doughnut', 'area']
      
      // Generate charts based on input type
      let result
      if (finalInputType === 'topic') {
        result = await generateChartDataFromTopic(query, llmService)
        // Store search results for reference modal
        setSearchResults(result.searchResults || [])
      } else {
        result = await generateChartDataFromInput(finalData)
        setSearchResults([])
      }
      
      const charts = result.charts || result // Handle both new and old format
      
      // Validate generated charts
      if (!validateChartData(charts)) {
        throw new Error('Generated chart data is invalid')
      }
      
      // Update progress and state for each chart
      for (let i = 0; i < charts.length; i++) {
        setProgress({ current: i + 1, total: 5 })
        setGeneratedCharts(prev => [...prev, charts[i]])
        
        // Small delay to show progress
        await new Promise(resolve => setTimeout(resolve, 500))
      }
      
      newGeneratedCharts.push(...charts)
      
      setProgress({ current: 5, total: 5 })
      setCurrentState(AGENT_STATES.COMPLETED)
      
      // Consume credits after successful generation
      await consumeCredits(AGENT_TYPES.CHART)
      
      // Save to agent history
      try {
        const saveResult = await AgentHistoryIntegration.saveChartResult({
          query: query,
          inputType: finalInputType,
          inputData: finalData,
          generatedCharts: newGeneratedCharts,
          searchResults: searchResults, // Include search results for references
          progress: { current: 5, total: 5 }
        })
        
        if (saveResult.success) {
          console.log('✅ Chart berhasil disimpan ke history:', saveResult.data)
        } else {
          console.warn('⚠️ Gagal menyimpan Chart ke history:', saveResult.error)
        }
      } catch (historyError) {
        console.warn('⚠️ Failed to save to agent history:', historyError)
      }
      
      console.log('✅ Chart generation completed successfully')
    } catch (error) {
      console.error('❌ Error during Chart generation:', error)
      
      // Provide specific error messages based on error type
      let errorMessage = 'Terjadi kesalahan saat membuat chart'
      
      if (error.message.includes('Tidak dapat menemukan data dari Search API')) {
        errorMessage = 'Tidak dapat menemukan data untuk topik tersebut. Silakan coba dengan topik yang lebih spesifik atau periksa koneksi internet Anda.'
      } else if (error.message.includes('Gagal memproses respons AI')) {
        errorMessage = 'AI tidak dapat memproses data untuk topik tersebut. Silakan coba dengan topik yang berbeda atau lebih spesifik.'
      } else if (error.message.includes('Gagal memproses data dengan AI')) {
        errorMessage = 'Terjadi kesalahan dalam pemrosesan data oleh AI. Silakan coba lagi atau gunakan topik yang berbeda.'
      } else if (error.message.includes('Fallback data generation is disabled')) {
        errorMessage = 'Sistem hanya menggunakan data asli dari Search API dan AI. Tidak dapat menghasilkan chart untuk topik ini. Silakan coba topik yang berbeda.'
      } else if (error.message) {
        errorMessage = error.message
      }
      
      setError(errorMessage)
      setCurrentState(AGENT_STATES.GENERATING)
    } finally {
      setIsLoading(false)
    }
  }

  // Removed duplicate functions - using chartUtils.js functions instead

  const handleRegenerateChart = async (chartId) => {
    const chart = generatedCharts.find(c => c.id === chartId)
    if (!chart) return

    try {
      setIsLoading(true)
      setError(null)
      
      console.log(`🔄 Regenerating chart: ${chart.type} for query: ${query}`)
      
      // Generate new charts using chartUtils functions with linkup and LLM integration
      let result
      if (inputType === 'topic') {
        result = await generateChartDataFromTopic(query, llmService)
        // Update search results for reference modal
        setSearchResults(result.searchResults || [])
      } else {
        result = await generateChartDataFromInput(chartData)
      }
      
      const newCharts = result.charts || result // Handle both new and old format
      
      // Find the chart with the same type as the one being regenerated
      const newChart = newCharts.find(c => c.type === chart.type) || newCharts[0]
      
      if (newChart) {
        const updatedChart = {
          ...chart,
          ...newChart,
          id: chart.id // Keep the original ID
        }
        
        setGeneratedCharts(prev => prev.map(c => c.id === chartId ? updatedChart : c))
        console.log(`✅ Successfully regenerated ${chart.type} chart`)
      } else {
        throw new Error('Failed to generate new chart data')
      }
    } catch (error) {
      console.error('❌ Error regenerating chart:', error)
      
      // Provide specific error messages for regeneration
      let errorMessage = 'Gagal regenerate chart. Silakan coba lagi.'
      
      if (error.message.includes('Tidak dapat menemukan data dari Search API')) {
        errorMessage = 'Tidak dapat menemukan data terbaru untuk regenerasi chart. Silakan periksa koneksi internet dan coba lagi.'
      } else if (error.message.includes('Gagal memproses respons AI')) {
        errorMessage = 'AI tidak dapat memproses data untuk regenerasi chart. Silakan coba lagi.'
      } else if (error.message.includes('Fallback data generation is disabled')) {
        errorMessage = 'Sistem hanya menggunakan data asli. Tidak dapat regenerasi chart saat ini. Silakan coba lagi nanti.'
      } else if (error.message) {
        errorMessage = error.message
      }
      
      setError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  // Render based on current state
  switch (currentState) {
    
    case AGENT_STATES.GENERATING:
    case AGENT_STATES.COMPLETED:
      return (
        <ChartResultPage
          query={query}
          inputType={inputType}
          inputData={chartData}
          generatedCharts={generatedCharts}
          progress={progress}
          isLoading={isLoading}
          isCompleted={currentState === AGENT_STATES.COMPLETED}
          onBack={onBack}
          onRegenerateChart={handleRegenerateChart}
          error={error}
          searchResults={searchResults}
        />
      )
    
    default:
      return null
  }
}

ChartAgent.propTypes = {
  query: PropTypes.string.isRequired,
  onBack: PropTypes.func.isRequired,
  llmService: PropTypes.object.isRequired
}

export default ChartAgent